import { useState, useEffect } from "react";
import { router } from "../data/Services/ordersRouter.jsx";

/**
 * Hook to track router connection status reactively.
 * Uses receiver pattern + periodic polling to ensure UI updates when router state changes.
 *
 * @returns {Object} { isConnected: boolean, broker: string }
 */
export function useRouterStatus() {
  const [isConnected, setIsConnected] = useState(false);
  const [broker, setBroker] = useState("");

  useEffect(() => {
    // Determine if router is fully connected and operational
    const checkRouterStatus = () => {
      if (!router) {
        setIsConnected(false);
        setBroker("");
        return;
      }

      const isRouterOnline = router.state === "online";
      const isRouterNotIdle = !router.idle;
      const hasValidConnection =
        typeof router.isConnectionOK !== "function" || router.isConnectionOK();

      setIsConnected(isRouterOnline && isRouterNotIdle && hasValidConnection);
      setBroker(router.broker || "");
    };

    // Router receiver for connection events
    const routerReceiver = {
      OnConnected: () => {
        checkRouterStatus();
      },
      OnDisconnected: () => {
        checkRouterStatus();
      },
      // Also listen for account data as indicator of successful connection
      OnAccountData: () => {
        checkRouterStatus();
      },
    };

    // Register receiver
    if (router && router.registerReceiver) {
      router.registerReceiver(routerReceiver);
    }

    // Initial check
    checkRouterStatus();

    // Periodic fallback check (every 2 seconds) for tablets where events may be missed
    const statusInterval = setInterval(checkRouterStatus, 2000);

    // Cleanup
    return () => {
      clearInterval(statusInterval);
      if (router && router.unregisterReceiver) {
        router.unregisterReceiver(routerReceiver);
      }
    };
  }, []);

  return { isConnected, broker };
}

export default useRouterStatus;
